import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  computeFlightPlan,
  type LoadDataInput,
  type PlanLegInput,
  type SavePlanInput,
} from '@jupi/shared'
import { useCurrentPlan, useSaveLoad, useSavePlan, type CurrentPlan } from '../../api/plan'

// The flight plan as the pilot is editing it. Edits land in a local draft
// at once -- the figures below are recomputed from it on every keystroke
// with the same shared computeFlightPlan the API uses -- and are saved a
// moment after the pilot stops. At an airfield with no signal the save
// fails quietly, the draft stays, and it is sent again when the phone is
// back online.

export type SaveState = 'saved' | 'saving' | 'unsaved'
export type PlanDraft = SavePlanInput & { legs: PlanLegInput[] }

const SAVE_DELAY_MS = 800
const RESERVE_KEY = 'jupi.plan.reserveMinutes'

// The final reserve is the rules' minimum: 30 min VFR, 45 min IFR.
export const VFR_RESERVE_MINUTES = 30
export const IFR_RESERVE_MINUTES = 45

function fromServer(data: CurrentPlan): PlanDraft {
  const { plan } = data
  return {
    registration: plan.registration,
    windDir: plan.windDir,
    windSpeedKt: plan.windSpeedKt,
    localRadiusNm: plan.localRadiusNm,
    localMinutes: plan.localMinutes,
    legs: plan.legs.map(({ position: _position, ...leg }) => leg),
  }
}

// A value saved a moment after it stops changing, retried when the device
// comes back online.
function useAutosave<T>(save: (value: T) => Promise<unknown>) {
  const [state, setState] = useState<SaveState>('saved')
  const pending = useRef<T | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const saveRef = useRef(save)
  saveRef.current = save

  const flush = useCallback(async () => {
    clearTimeout(timer.current)
    const value = pending.current
    if (value === null) return
    setState('saving')
    try {
      await saveRef.current(value)
      if (pending.current === value) {
        pending.current = null
        setState('saved')
      }
    } catch {
      setState('unsaved')
    }
  }, [])

  const schedule = useCallback(
    (value: T) => {
      pending.current = value
      setState('unsaved')
      clearTimeout(timer.current)
      timer.current = setTimeout(() => void flush(), SAVE_DELAY_MS)
    },
    [flush],
  )

  useEffect(() => {
    const online = () => void flush()
    window.addEventListener('online', online)
    return () => {
      window.removeEventListener('online', online)
      // Leaving the page with an edit still waiting: send it now.
      void flush()
    }
  }, [flush])

  const dirty = useCallback(() => pending.current !== null, [])
  return useMemo(() => ({ state, schedule, flush, dirty }), [state, schedule, flush, dirty])
}

export function useReserveMinutes() {
  const [minutes, setMinutes] = useState(() => {
    try {
      return window.localStorage.getItem(RESERVE_KEY) === String(IFR_RESERVE_MINUTES) ? IFR_RESERVE_MINUTES : VFR_RESERVE_MINUTES
    } catch {
      return VFR_RESERVE_MINUTES
    }
  })
  const set = (value: number) => {
    setMinutes(value)
    try {
      window.localStorage.setItem(RESERVE_KEY, String(value))
    } catch {
      // Private mode: the reserve just is not remembered.
    }
  }
  return [minutes, set] as const
}

export function usePlanDraft() {
  const query = useCurrentPlan()
  const savePlan = useSavePlan()
  const saveLoad = useSaveLoad()
  const [draft, setDraft] = useState<PlanDraft | null>(null)
  const [load, setLoad] = useState<LoadDataInput | null>(null)
  const [reserveMinutes, setReserveMinutes] = useReserveMinutes()
  const planSave = useAutosave((value: PlanDraft) => savePlan.mutateAsync(value))
  const loadSave = useAutosave((value: LoadDataInput) => saveLoad.mutateAsync(value))

  // Take the server's copy whenever there is no local edit in flight: on
  // first load, and after a saved route is loaded into the plan.
  const planDirty = planSave.dirty
  const loadDirty = loadSave.dirty
  useEffect(() => {
    if (!query.data) return
    if (!planDirty()) setDraft(fromServer(query.data))
    if (!loadDirty()) setLoad(query.data.load)
  }, [query.data, planDirty, loadDirty])

  // Computed from the draft as rendered, not inside a state updater, so
  // StrictMode's double-invoked updaters cannot schedule a save twice.
  const { schedule: schedulePlan, flush: flushPlan } = planSave
  const updatePlan = useCallback(
    (change: (current: PlanDraft) => PlanDraft, options: { immediately?: boolean } = {}) => {
      if (!draft) return
      const changed = change(draft)
      // A local flight is one point and a time; a second point makes it a route.
      const next = changed.legs.length > 1 ? { ...changed, localRadiusNm: null, localMinutes: null } : changed
      setDraft(next)
      schedulePlan(next)
      // Changing the aircraft: its figures come back with the save.
      if (options.immediately) void flushPlan()
    },
    [draft, schedulePlan, flushPlan],
  )

  const { schedule: scheduleLoad } = loadSave
  const updateLoad = useCallback(
    (change: Partial<LoadDataInput>) => {
      if (!load) return
      const next = { ...load, ...change }
      setLoad(next)
      scheduleLoad(next)
    },
    [load, scheduleLoad],
  )

  // The aircraft's figures come with the server's copy; until a changed
  // aircraft has been saved, there are none to compute with.
  const aircraft =
    query.data?.aircraft && query.data.aircraft.registration === draft?.registration ? query.data.aircraft : null

  const figures = useMemo(() => {
    if (!draft || !load) return null
    return computeFlightPlan({
      legs: draft.legs.map((leg) => ({
        lat: leg.lat,
        lon: leg.lon,
        variation: leg.variation ?? null,
        windDir: leg.windDir,
        windSpeedKt: leg.windSpeedKt,
        tasKt: leg.tasKt ?? null,
      })),
      aircraft,
      load,
      memberCruise: draft.registration ? (query.data?.cruiseOverrides[draft.registration] ?? null) : null,
      reserveMinutes,
      localMinutes: draft.localRadiusNm ? draft.localMinutes : null,
    })
  }, [draft, load, aircraft, query.data?.cruiseOverrides, reserveMinutes])

  const saveState: SaveState =
    planSave.state === 'unsaved' || loadSave.state === 'unsaved'
      ? 'unsaved'
      : planSave.state === 'saving' || loadSave.state === 'saving'
        ? 'saving'
        : 'saved'

  return {
    isLoading: query.isLoading,
    error: query.error,
    draft,
    load,
    aircraft,
    cruiseOverrides: query.data?.cruiseOverrides ?? {},
    updatedAt: query.data?.plan.updatedAt ?? null,
    figures,
    reserveMinutes,
    setReserveMinutes,
    updatePlan,
    updateLoad,
    saveState,
  }
}
